import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";
import { Heading, Subheading } from "../../design/heading";
import LoadingView from "../../components/LoadingView";
import { Avatar } from "../../design/avatar";
import ErrorView from "../../components/ErrorView";
import Breadcrumbs from "../../components/Breadcrumbs";
import UserMatterList from "./components/UserMatterList";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../design/description-list";
import EditUserButton from "./components/EditUserButton";
import DeleteUserButton from "./components/DeleteUserButton";

const UserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { selectedUser, setSelectedUser, error, loading } = useUser();

  useEffect(() => {
    if (userId) {
      setSelectedUser(userId);
    }
  }, [userId]);

  if (loading.details) {
    return <LoadingView title="Loading User" />;
  }

  if (error.details) {
    return <ErrorView error={error.details} />;
  }

  if (!selectedUser) {
    return <ErrorView error={new Error("User not found")} />;
  }

  return (
    <div>
      <Breadcrumbs
        items={[
          { label: "Users", href: "/users" },
          {
            label: selectedUser.name ?? "User",
            href: `/user/${selectedUser._id}`,
          },
        ]}
      />
      <div className="flex flex-col w-full gap-6 md:flex-row">
        <div className="w-full gap-6">
          <div>
            <div className="flex items-end justify-between gap-4">
              <div className="flex gap-4">
                {selectedUser.avatarUrl && (
                  <Avatar
                    src={selectedUser.avatarUrl}
                    alt={selectedUser.firstName}
                    className="w-12 h-12 rounded-full"
                    initials={selectedUser.initials}
                  />
                )}
                <Heading>{selectedUser.firstName}</Heading>
              </div>
              <div className="flex gap-2">
                <EditUserButton user={selectedUser} />
                <DeleteUserButton userId={selectedUser._id} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Subheading>User Details</Subheading>
      <DescriptionList>
        {selectedUser.email && (
          <>
            <DescriptionTerm>Email</DescriptionTerm>
            <DescriptionDetails>{selectedUser.email}</DescriptionDetails>
          </>
        )}
        {selectedUser.phoneNumber && (
          <>
            <DescriptionTerm>Phone Number</DescriptionTerm>
            <DescriptionDetails>{selectedUser.phoneNumber}</DescriptionDetails>
          </>
        )}
      </DescriptionList>
      <UserMatterList />
    </div>
  );
};

export default UserDetailPage;
